/**
 * BlogPostPage - Individual blog post with Article schema for AI SEO
 */
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Tag, Share2, Facebook, Twitter, Linkedin, Copy, Check, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import SEO from '@/components/SEO';
import { BreadcrumbSchema } from '@/components/StructuredData';
import api from '@/utils/apiClient';
import { toast } from 'sonner';

// BlogPosting Schema for enhanced Google rich results
function BlogPostingSchema({ post }) {
  if (!post) return null;
  
  // Estimate word count for Google
  const wordCount = post.content?.split(/\s+/).length || 0;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "alternativeHeadline": post.meta_title || post.title,
    "description": post.meta_description || post.excerpt,
    "image": {
      "@type": "ImageObject",
      "url": post.featured_image,
      "width": 1200,
      "height": 630
    },
    "author": {
      "@type": "Organization",
      "name": post.author || "Bulaccino Cafe",
      "url": "https://bulaccinocafe.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://bulaccinocafe.com/logo.png"
      }
    },
    "publisher": {
      "@type": "Organization",
      "name": "Bulaccino Cafe",
      "url": "https://bulaccinocafe.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://bulaccinocafe.com/logo.png",
        "width": 200,
        "height": 60
      },
      "sameAs": [
        "https://www.facebook.com/bulaccinocafe",
        "https://www.instagram.com/bulaccinocafe",
        "https://www.tiktok.com/@bulaccinocafe"
      ]
    },
    "datePublished": post.created_at,
    "dateModified": post.updated_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://bulaccinocafe.com/blog/${post.slug}`
    },
    "url": `https://bulaccinocafe.com/blog/${post.slug}`,
    "keywords": post.tags?.join(", "),
    "articleSection": post.category,
    "articleBody": post.content?.replace(/[#*\-_]/g, '').substring(0, 500),
    "wordCount": wordCount,
    "inLanguage": "en-FJ",
    "isAccessibleForFree": true,
    "about": {
      "@type": "Thing",
      "name": post.category === "coffee" ? "Coffee" : 
             post.category === "food" ? "Food & Dining" :
             post.category === "travel" ? "Fiji Travel" :
             post.category === "experience" ? "Fiji Experiences" :
             post.category === "news" ? "Cafe News" :
             "Bulaccino Cafe"
    },
    "mentions": [
      {
        "@type": "Place",
        "name": "Bulaccino Cafe",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Shop 3 at Concave Road",
          "addressLocality": "Namaka Nadi",
          "addressRegion": "Western Division",
          "postalCode": "",
          "addressCountry": "FJ"
        }
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await api.get(`/blog/posts/${slug}`);
        setPost(res.data);
        
        // Fetch related posts from same category
        const relatedRes = await api.get(`/blog/posts?category=${res.data.category}&limit=3`);
        setRelatedPosts(relatedRes.data.filter(p => p.id !== res.data.id).slice(0, 2));
      } catch (err) {
        console.error('Failed to fetch post:', err);
        navigate('/blog');
      }
      setLoading(false);
    };
    fetchPost();
  }, [slug, navigate]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateReadTime = (content) => {
    const words = content?.split(/\s+/).length || 0;
    return Math.max(1, Math.ceil(words / 200));
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = post?.title || '';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-3/4" />
            <div className="aspect-video bg-muted rounded-2xl" />
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded w-full" />
              <div className="h-4 bg-muted rounded w-full" />
              <div className="h-4 bg-muted rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  const breadcrumbs = [
    { name: 'Home', url: 'https://bulaccinocafe.com' },
    { name: 'Blog', url: 'https://bulaccinocafe.com/blog' },
    { name: post.title, url: `https://bulaccinocafe.com/blog/${post.slug}` }
  ];

  // Convert markdown-style content to HTML (basic)
  const renderContent = (content) => {
    if (!content) return '';
    
    // Convert markdown headers, bold, etc.
    let html = content
      .replace(/^### (.*$)/gim, '<h3 class="font-heading text-xl mt-8 mb-4">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="font-heading text-2xl mt-10 mb-4">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="font-heading text-3xl mt-12 mb-6">$1</h1>')
      .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
      .replace(/\*(.*)\*/gim, '<em>$1</em>')
      .replace(/^- (.*$)/gim, '<li class="ml-4">$1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 list-decimal">$1</li>')
      .replace(/\n\n/g, '</p><p class="mb-4 leading-relaxed">')
      .replace(/---/g, '<hr class="my-8 border-border" />');
    
    return `<p class="mb-4 leading-relaxed">${html}</p>`;
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="blog-post-page">
      <SEO 
        title={post.meta_title || `${post.title} | Bulaccino Cafe Blog`}
        description={post.meta_description || post.excerpt}
        keywords={post.tags?.join(', ')}
        canonical={`https://bulaccinocafe.com/blog/${post.slug}`}
        image={post.featured_image}
      />
      <BlogPostingSchema post={post} />
      <BreadcrumbSchema items={breadcrumbs} />

      <article className="max-w-3xl mx-auto px-6 md:px-12">
        {/* Back Link */}
        <Link 
          to="/blog" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 text-sm text-muted-foreground mb-4">
            <span className="px-3 py-1 bg-primary/10 text-primary rounded-full font-medium capitalize">
              {post.category}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(post.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {estimateReadTime(post.content)} min read
            </span>
          </div>
          
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-4">
            {post.title}
          </h1>
          
          <p className="text-lg text-muted-foreground mb-6">
            {post.excerpt}
          </p>

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="w-4 h-4" />
              By {post.author}
            </div>
            
            {/* Share Buttons */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground mr-1">Share:</span>
              <button
                onClick={() => handleShare('facebook')}
                className="p-2 rounded-full hover:bg-muted transition-colors"
                aria-label="Share on Facebook"
              >
                <Facebook className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleShare('twitter')}
                className="p-2 rounded-full hover:bg-muted transition-colors"
                aria-label="Share on Twitter"
              >
                <Twitter className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="p-2 rounded-full hover:bg-muted transition-colors"
                aria-label="Share on LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </button>
              <button
                onClick={handleCopyLink}
                className="p-2 rounded-full hover:bg-muted transition-colors"
                aria-label="Copy link"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </motion.header>

        {/* Featured Image */}
        {post.featured_image && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="mb-10"
          >
            <img 
              src={post.featured_image} 
              alt={post.title}
              className="w-full rounded-2xl shadow-lg"
            />
          </motion.div>
        )}

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="prose prose-lg dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: renderContent(post.content) }}
        />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-10 pt-6 border-t border-border">
            <div className="flex items-center flex-wrap gap-2">
              <Tag className="w-4 h-4 text-muted-foreground" />
              {post.tags.map(tag => (
                <Link
                  key={tag}
                  to={`/blog?tag=${tag}`}
                  className="px-3 py-1 bg-muted rounded-full text-sm text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-12 pt-8 border-t border-border">
            <h3 className="font-heading text-xl mb-6">Related Articles</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map(related => (
                <Link
                  key={related.id}
                  to={`/blog/${related.slug}`}
                  className="group bg-card rounded-xl border border-border overflow-hidden hover:border-primary/50 transition-all"
                >
                  {related.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={related.featured_image} 
                        alt={related.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h4 className="font-medium group-hover:text-primary transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 p-8 bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl text-center">
          <h3 className="font-heading text-xl mb-3">
            Ready for the Best Coffee in Fiji?
          </h3>
          <p className="text-muted-foreground mb-4">
            Experience everything we write about at Bulaccino Cafe.
          </p>
          <Link to="/menu">
            <Button className="rounded-full">
              View Our Menu
            </Button>
          </Link>
        </div>
      </article>
    </div>
  );
}
